<template>
    <div>
        <loading v-show="loading"></loading>

        <div v-show="!loading">

            <div class="flex justify-end">
                <confirm-dialog @confirmed="remove">
                    <template slot="header">Benutzergruppe löschen</template>
                    <template slot="question">
                        Soll die Benutzergruppe
                        <span class="font-bold">{{ name }}</span>
                        wirklich gelöscht werden?
                    </template>
                    <template slot="confirm">
                        <button-delete>
                            <template slot="icon"><i class="fa fa-trash"></i></template>
                            Benutzergruppe löschen
                        </button-delete>
                    </template>
                    <button-delete>
                        <template slot="icon"><i class="fa fa-trash"></i></template>
                        Benutzergruppe löschen
                    </button-delete>
                </confirm-dialog>
            </div>

            <full-card>
                <template v-slot:header="">Allgemeine Informationen</template>

                <input-text class="mb-4" v-model="name">
                    Gruppenname
                </input-text>

                <input-text class="mb-4" v-model="nameInternal">
                    Interner Gruppenname
                    <template v-slot:helper>Wird nur im Dashboard angezeigt.</template>
                </input-text>

                <toggle-group class="mb-4" v-model="isDefault">
                    Standardgruppe
                    <template v-slot:helper>
                        Alle Nutzer ohne explizite Gruppe werden hier Mitglied
                    </template>
                </toggle-group>

                <toggle-group class="mb-4" v-model="isInternal">
                    nur Intern
                    <template v-slot:helper>
                        Diese Gruppe ist nur im Dashboard sichtbar (z.B. für interne Angelegenheiten)
                    </template>
                </toggle-group>

                <div class="grid grid-cols-1 gap-4">
                    <user-list-selection v-model="checkedUsers" :users="this.users" :loading-users="this.loadingUsers"></user-list-selection>

                    <toggle-group v-model="hasConfirmationFreeConferenceRooms">
                        Bestätigungsfreies buchen von Konferenzräumen
                        <template v-slot:helper>Nutzer in der Gruppe dürfen Konferenzräume ohne Bestätigung buchen.</template>
                    </toggle-group>

                    <div>
                        Bestätigungsfreie Kategorien in der Gruppe:
                        <loading v-show="loadingCategories"></loading>
                        <div v-for="u in availableCategories" v-show="!loadingCategories">
                            <label>
                                <input class="mr-2" type="checkbox" :value="u.id" v-model="checkedCategories">
                                {{ u.name }}
                            </label>
                        </div>
                    </div>
                </div>

                <div class="pt-2">
                    <loading v-show="loadingFloors"></loading>
                    <div v-if="!loadingFloors">
                        <multi-select-small class="mb-4" v-model="ignoredFloors"
                                            :values="floors.map(a => a.id)"
                                            :text="floors.map(a => a.text)"
                                            :disabled="floors.map(a => a.disabled)"
                        >
                            Ausgenommene Etagen
                            <template v-slot:helper>Die selektierten Etagen, werden von den Benutzern in dieser Benutzergruppe ausgeblendet</template>
                        </multi-select-small>
                    </div>
                </div>

                <div class="flex justify-end">
                    <button-primary @click="save">
                        <template slot="icon"><i class="fa fa-save"></i></template>
                        Speichern
                    </button-primary>
                </div>
            </full-card>

            <full-card class="mt-8">
                <template v-slot:header>Favorisierte Schreibtische (Homezone)</template>

                <div class="mb-8">
                    Schreibtische in der Homezone:
                    <loading v-show="loadingCategories"></loading>
                    <div v-for="d in availableDesks" v-show="!loadingDesks">
                        <label>
                            <input class="mr-2" type="checkbox" :value="d.id" v-model="deskFavorites">
                            {{ d.name }}
                        </label>
                    </div>
                </div>

                <div class="flex justify-end">
                    <button-primary @click="saveHomezone">
                        <template slot="icon"><i class="fa fa-save"></i></template>
                        Speichern
                    </button-primary>
                </div>
            </full-card>
        </div>
    </div>
</template>

<script>
import ButtonPrimary from "./UI/ButtonPrimary";
import Modal from "./Modal";
import ButtonWhite from "./UI/ButtonWhite";
import Loading from "./UI/Loading";
import InputText from "./UI/InputText";
import ToggleGroup from "./UI/ToggleGroup";
import ButtonDelete from "./UI/ButtonDelete";
import ConfirmDialog from "./UI/ConfirmDialog";
import FullCard from "./UI/FullCard.vue";
import UserListSelection from "./UserListSelection.vue";
import SelectMenu from "./UI/SelectMenu.vue";
import MultiSelectSmall from "./UI/MultiSelectSmall.vue";

export default {
    name: "UserGroupEdit",
    components: {
        MultiSelectSmall,
        SelectMenu,
        UserListSelection,
        FullCard,
        ConfirmDialog,
        ButtonDelete,
        ToggleGroup,
        InputText,
        Loading,
        ButtonWhite,
        Modal,
        ButtonPrimary
    },

    props: ['id'],

    data: () => ({
        loading: false,
        loadingDesks: false,
        loadingUsers: false,
        loadingCategories: false,
        loadingFloors: false,
        hasConfirmationFreeConferenceRooms: false,
        name: '',
        nameInternal: '',
        isDefault: false,
        isInternal: false,
        checkedUsers: [],
        availableUsers: [],
        checkedCategories: [],
        availableCategories: [],
        deskFavorites: [],
        availableDesks: [],
        ignoredFloors: [],
        availableFloors: [],
    }),

    mounted() {
        this.load();
    },

    computed: {
        users() {
            return this.availableUsers;
        },
        floors() {
            return this.availableFloors;
        }
    },

    methods: {
        load() {
            this.loading = true;
            this.$http
                .get(`/admin/user_groups/${this.id}`)
                .then(result => {
                    this.loading = false;
                    this.name = result.data.data.name;
                    this.nameInternal = result.data.data.name_internal;
                    this.isDefault = result.data.data.is_default;
                    this.isInternal = result.data.data.is_internal;
                    this.checkedUsers = result.data.data.users;
                    this.deskFavorites = result.data.data.desk_favorites;
                    this.checkedCategories = result.data.data.confirmation_free_bookable_categories;
                    this.hasConfirmationFreeConferenceRooms = result.data.data.has_confirmation_free_conference_rooms;
                    this.ignoredFloors = result.data.data.ignored_floors ?? [];
                })
                .catch(error => {
                    this.$root.$emit('error', error);
                    this.loading = false;
                });

            this.loadingUsers = true;
            this.$http.get('/admin/users/available')
                .then(result => {
                    this.loadingUsers = false;
                    this.availableUsers = result.data.data;
                })
                .catch(error => {
                    this.$root.$emit('error', error);
                    this.loadingUsers = false;
                });

            this.loadingDesks = true;
            this.$http.get('/admin/desks')
                .then(result => {
                    this.loading = false;
                    this.availableDesks = result.data.desks;
                    this.loadingDesks = false;
                })
                .catch(error => {
                    this.$root.$emit('error', error);
                    this.loadingDesks = false;
                });

            this.loadingCategories = true
            this.$http.get('/admin/bookable_categories')
                .then(result => {
                    this.loading = false;
                    this.loadingCategories = false;
                    this.availableCategories = result.data.data;
                })

            this.loadingFloors = true;
            this.$http.get('/admin/floors')
                .then(result => {
                    this.loading = false;
                    this.availableFloors = result.data.data.map(v => {
                        return {
                            id: v.id,
                            text: v.name,
                            disabled: v.is_blocked,
                        }
                    });
                    this.loadingFloors = false;
                })
                .catch(error => {
                    this.$root.$emit('error', error);
                    this.loadingFloors = false;
                });
        },
        save() {
            this.$http.post(`/admin/user_groups/${this.id}`, {
                _method: 'put',
                name: this.name,
                name_internal: this.nameInternal,
                users: this.checkedUsers,
                is_default: this.isDefault,
                is_internal: this.isInternal,
                confirmation_free_bookable_categories: this.checkedCategories,
                has_confirmation_free_conference_rooms: this.hasConfirmationFreeConferenceRooms,
                ignored_floors: this.ignoredFloors ?? []
            }).then(() => {
                this.loading = false;
                this.showModal = false;
                this.$emit('updated');
                this.$root.$emit('alert', 'Gespeichert.');
            }).catch(error => {
                this.$root.$emit('error', error);
            })
        },
        saveHomezone() {
            this.$http.post(`/admin/user_groups/${this.id}`, {
                _method: 'put',
                desk_favorites: this.deskFavorites,
            }).then(() => {
                this.loading = false;
                this.showModal = false;
                this.$emit('updated');
                this.$root.$emit('alert', 'Gespeichert.');
            }).catch(error => {
                this.$root.$emit('error', error);
            })
        },
        remove() {
            this.loading = true;
            this.$http.post(`/admin/user_groups/${this.id}`, {
                _method: 'delete',
            }).then(() => {
                this.loading = false;
                this.showModal = false;
                this.$emit('updated');
                this.$root.$emit('alert', 'Nutzergruppe gelöscht.');
            }).catch(error => {
                this.loading = false;
                this.$root.$emit('error', error);
            });
        },
    },
}
</script>

<style scoped>

</style>
